import { Node, mergeAttributes, VueNodeViewRenderer } from '@tiptap/vue-3'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import EditorTemplateVariable from './NodeViews/EditorTemplateVariable.vue'
import type { TemplateVariable } from '@/questionnaire/types'
import { ref, type Ref } from 'vue'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    templateVariable: {
      /**
       * Toggle a template Variable
       */
      setTemplateVariable: (name: string) => ReturnType
      /**
       * Open the template variable editor
       */
      openTemplateVariableEditor: () => ReturnType
      /**
       * Open template variables dropdown at current selection
       */
      openTemplateVariablesDropdown: () => ReturnType
      /**
       * Replace current selection with template variable
       */
      replaceWithTemplateVariable: (name: string) => ReturnType
    }
  }
}

export interface TemplateVariableOptions {
  HTMLAttributes: Record<string, unknown>
  suggestion: Omit<SuggestionOptions, 'editor'>
  templateVariables: Ref<TemplateVariable[]>
}

export const ExtensionTemplateVariable = Node.create<TemplateVariableOptions>({
  name: 'templateVariable',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,
  addNodeView() {
    return VueNodeViewRenderer(EditorTemplateVariable)
  },
  addOptions() {
    return {
      HTMLAttributes: { class: 'template-variable' },
      templateVariables: ref([]),
      suggestion: {
        char: '$',
        command: ({ editor, range, props }) => {
          const nodeAfter = editor.view.state.selection.$to.nodeAfter
          const overrideSpace = nodeAfter?.text?.startsWith(' ')
          if (overrideSpace) {
            range.to += 1
          }
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              { type: 'text', text: ' ' },
            ])
            .run()

          window.getSelection()?.collapseToEnd()
        },
        allow: ({ state, range }) => {
          const from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!from.parent.type.contentMatch.matchType(type)
          return allow
        },
      },
    }
  },
  addAttributes() {
    return {
      name: {
        default: null,
        renderHTML: attributes => {
          return {
            'data-template-variable-name': attributes.name,
          }
        },
        parseHTML: element => element.getAttribute('data-template-variable-name'),
      },
    }
  },
  parseHTML() {
    return [{ tag: `span[data-type="${this.name}"` }]
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['span', mergeAttributes({ 'data-type': this.name }, HTMLAttributes, this.options.HTMLAttributes), `$:${node.attrs.name}:$`]
  },
  renderText({ node }) {
    return `TV:${node.attrs.name}`
  },
  addCommands() {
    return {
      setTemplateVariable:
        name =>
        ({ commands }) => {
          return commands.insertContent({ type: 'templateVariable', attrs: { name } })
        },
      openTemplateVariableEditor:
        () =>
        ({ editor, state }) => {
          const { from } = state.selection
          editor.view.dom.dispatchEvent(
            new CustomEvent('open-template-variable-editor', {
              bubbles: true,
              detail: { position: from, isNodeView: false },
            })
          )
          return true
        },
      openTemplateVariablesDropdown:
        () =>
        ({ editor, state }) => {
          const { from, to } = state.selection
          console.log('openTemplateVariablesDropdown', from, to)
          // Check if there's a text selection
          if (from !== to) {
            // Get the selection coordinates for positioning the dropdown
            const coords = editor.view.coordsAtPos(from)

            editor.view.dom.dispatchEvent(
              new CustomEvent('open-template-variables-dropdown', {
                bubbles: true,
                detail: {
                  from,
                  to,
                  coords,
                  hasSelection: true,
                  templateVariables: this.options.templateVariables.value,
                },
              })
            )
          } else {
            // No selection, just show dropdown at cursor position
            const coords = editor.view.coordsAtPos(from)

            editor.view.dom.dispatchEvent(
              new CustomEvent('open-template-variables-dropdown', {
                bubbles: true,
                detail: {
                  from,
                  to: from,
                  coords,
                  hasSelection: false,
                  templateVariables: this.options.templateVariables.value,
                },
              })
            )
          }
          return true
        },
      replaceWithTemplateVariable:
        name =>
        ({ editor, state }) => {
          const { from, to } = state.selection

          if (from !== to) {
            // Replace the current selection with a template variable
            editor.chain().focus().deleteRange({ from, to }).insertContent({ type: 'templateVariable', attrs: { name } }).run()
          } else {
            // Just insert at current position
            editor.commands.setTemplateVariable(name)
          }
          return true
        },
    }
  },
  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
export default ExtensionTemplateVariable
